import authInfoSource, { AuthInfoSource } from 'utils/AuthInfoSource';
import { Account, Actor, AccessToken, RoleNames } from './Account';
import { UpdateEventListener, FireableUpdateEventListener } from 'utils/UpdateEventListener';
import { AccountWebService, RestfulAccountWebService } from 'ws/AccountWebService';
import i18n from 'i18n';
import { hasRoles } from 'core/permission/PermissionDSL';
import { SessionStorageHelper, SessionStorageItemKeys } from 'helper/StorageHelper';
import { OAuthPCHState } from 'core/oauth/OAuthPCH';
import config from 'config';
import { OAuthWebService, RestfulOAuthWebService } from 'ws/OAuthWebService';
import { isEmpty, uniq } from 'lodash';
import { SiteAnnouncement } from 'core/siteAnnouncement/SiteAnnouncement';

export interface AuthenticationManager {

  readonly logined: boolean;
  readonly actor: Actor | null;
  readonly account: Account | null;
  readonly event: UpdateEventListener<AuthenticationManager>;

  /**
   * Log out the current session.
   */
  logout (): void;

  /**
   * Switch to the specified actor as the current actor.
   *
   * @param actorId actor ID
   * @returns the actor promise
   */
  switchActor (actorId: number): void;

  /**
   * Fetch the account information from the server.
   *
   * @returns the account promise
   */
  fetchAccount (): Promise<Account>;

  /**
   * Log in with the given email and password.
   *
   * @param email the user email
   * @param password the user password
   * @returns the account promise
   */
  login (email: string, password: string): Promise<Account>;

  /**
   *
   * @param accountId the user accountId
   */
  sudo (accountId: number);

  /**
   * Redirect to the OAuth login page.
   */
  oauthRedirect (): Promise<void>;

  /**
   * Login with the OAuth grant code.
   *
   * @param code OAuth grant code
   * @param state OAuth state
   * @param hmacState Encrypted OAuth state hmacState
   */
  oauthLogin (code: string, state: string, hmacState: string): Promise<Account>;

  validateGuestToken (token: string): Promise<{
    userName: string,
    registerStatus: number
  }>;

  setUpPasswordFirstTime (token: string, password: string): Promise<void>;

  sendResetPasswordMail (email: string): Promise<void>;

  hasReadAnnouncement (announcement: SiteAnnouncement): boolean;
  markAnnouncementAsRead (announcement: SiteAnnouncement): void;

  hasSeenCampaignDashboardWalkthrough (): boolean;
  markCampaignDashboardWalkthroughSeen (): void;
}

const SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY = 'siteAnnouncementReadLogs';
const CAMPAIGN_DASHBOARD_WALKTHROUGH_SEEN_STORAGE_KEY = 'campaignDashboardWalkthroughSeen';

function findActor (account: Account, actorId: number): Actor | undefined {
  return account.actors.find(actor => actor.id === actorId);
}

export class DefaultAuthenticationManager implements AuthenticationManager {
  source: AuthInfoSource;
  currentActor: Actor | null;
  loginedAccount: Account | null;
  accountWebService: AccountWebService;
  oauthWebService: OAuthWebService;
  authEvent: FireableUpdateEventListener<AuthenticationManager>;

  constructor (
    source: AuthInfoSource = authInfoSource,
    accountWebService: AccountWebService = new RestfulAccountWebService(),
    oauthWebService: OAuthWebService = new RestfulOAuthWebService()
  ) {
    this.source = source;
    this.currentActor = null;
    this.loginedAccount = null;
    this.accountWebService = accountWebService;
    this.oauthWebService = oauthWebService;
    this.authEvent = new FireableUpdateEventListener<AuthenticationManager>();
  }

  get logined (): boolean {
    const token = this.source.getToken();
    return token.length > 0;
  }

  get event (): UpdateEventListener<AuthenticationManager> {
    return this.authEvent;
  }

  get actor (): Actor | null {
    return this.currentActor;
  }

  get account (): Account | null {
    return this.loginedAccount;
  }

  logout () {
    this.source.clear();
    this.currentActor = null;
    this.loginedAccount = null;
    this.notifyListeners();
  }

  async fetchAccount (): Promise<Account> {
    let account = await this.accountWebService.getAccount();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  switchActor (actorId: number) {
    if (this.currentActor !== null && this.currentActor.id === actorId) {
      return;
    }
    const target = this.switchableActor(actorId);
    this.updateActor(target);
    this.notifyListeners();
  }

  async login (email: string, password: string): Promise<Account> {
    const account = await this.accountWebService.login(email, password);
    this.source.clear();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  async oauthRedirect (): Promise<void> {
    const oauthConfig = config.oauthConfig;
    if (isEmpty(oauthConfig)) {
      throw new Error(i18n.t<string>('oauthLogin.errors.oauthLoginNotSupported'));
    }
    const { url, client_id, redirect_uri, scope, response_type } = oauthConfig;
    const oauthPCHState: OAuthPCHState = await this.oauthWebService.getState();
    const { hmacState, state } = oauthPCHState;

    // Store hmnacState in Cookies and expire in 10 minutes
    const expires = new Date(Date.now() + 10 * 60 * 1000);
    this.source.setHmacState(hmacState, expires.getTime());

    // Compute the OAuth login URL
    const oauthLoginUrl = `${url}?client_id=${client_id}&redirect_uri=${encodeURIComponent(redirect_uri)}&scope=${scope}&response_type=${response_type}&state=${state}`;
    window.location.assign(oauthLoginUrl);
  }

  async oauthLogin (code: string, state: string, hmacState: string): Promise<Account> {
    const account = await this.oauthWebService.oauthPCH(code, state, hmacState);
    this.source.clear();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  async sudo (accountId: number): Promise<Account> {
    const account = await this.accountWebService.sudo(accountId);
    this.source.clear();
    this.updateAccount(account);
    this.notifyListeners();
    return account;
  }

  async validateGuestToken (token: string): Promise<{
    userName: string,
    registerStatus: number
  }> {
    return this.accountWebService.validateGuestToken(token);
  }

  async setUpPasswordFirstTime (token: string, password: string): Promise<void> {
    return this.accountWebService.setUpPasswordFirstTime(token, password);
  }

  async sendResetPasswordMail (email: string): Promise<void> {
    return this.accountWebService.sendResetPasswordMail(email);
  }

  updateAccount (account: Account) {
    this.loginedAccount = account;
    const actorId = this.source.getActor();
    const actor = actorId !== null ? findActor(account, actorId) : account.actors[0];
    if (actor) {
      this.updateActor(actor);
    } else {
      throw new Error(i18n.t<string>('authenticationManager.errors.noActor'));
    }
  }

  updateActor (actor: Actor) {
    this.currentActor = actor;
    this.source.setActor(actor.id);
    SessionStorageHelper.removeItem(SessionStorageItemKeys.AGENCY);
    SessionStorageHelper.removeItem(SessionStorageItemKeys.ADVERTISER);
    if (actor &&
      hasRoles(RoleNames.adsAdmin, RoleNames.adsSales, RoleNames.adsReport).visible({ actor }) &&
      actor.advertiserId
    ) {
      SessionStorageHelper.setItem(SessionStorageItemKeys.ADVERTISER, actor.advertiserId);
    }
    if (actor.agencyId) {
      SessionStorageHelper.setItem(SessionStorageItemKeys.AGENCY, actor.agencyId);
    }
    this.updateAccessToken(actor.accessToken);
  }

  updateAccessToken (accessToken: AccessToken) {
    const token = accessToken.token;
    const expires = accessToken.expires;
    this.source.setToken(token, expires);
  }

  switchableActor (actorId: number): Actor {
    if (this.loginedAccount === null) {
      throw new Error('not logined yet');
    }
    const target = findActor(this.loginedAccount, actorId);
    if (target) {
      return target;
    } else {
      throw new Error('illegal actor ID');
    }
  }

  notifyListeners () {
    this.authEvent.fireEvent(this);
  }

  hasReadAnnouncement (announcement: SiteAnnouncement): boolean {
    if (!this.account || !this.actor) {
      return false;
    }

    if (this.actor.roleName === 'ADMIN') {
      return true;
    }

    const readLogs = this.getAnnouncementReadLogs(SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY);
    const userKey = `${this.account.id}`;
    const readLogsOfUser = Array.isArray(readLogs[userKey]) ? readLogs[userKey] : [];
    const announcementKey = `${announcement.id}_${announcement.version}`;
    return readLogsOfUser.includes(announcementKey);
  }

  markAnnouncementAsRead (announcement: SiteAnnouncement): void {
    if (!this.account || !this.actor) {
      return;
    }

    const readLogs = this.getAnnouncementReadLogs(SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY);
    const userKey = `${this.account.id}`;
    const announcementKeyPrefix = `${announcement.id}_`;
    // remove all previous versions of the announcement
    const readLogsOfUser = Array.isArray(readLogs[userKey]) ? readLogs[userKey] : [];
    readLogs[userKey] = readLogsOfUser.filter((key: string) => !key.startsWith(announcementKeyPrefix));
    // add the current version of the announcement
    const announcementKey = `${announcement.id}_${announcement.version}`;
    readLogs[userKey] = uniq([...readLogs[userKey], announcementKey]);
    localStorage.setItem(SITE_ANNOUNCEMENT_READ_LOGS_STORAGE_KEY, JSON.stringify(readLogs));
  }

  getAnnouncementReadLogs (storageKey: string): Record<string, string[]> {
    const announcementsReadLogs = localStorage.getItem(storageKey) || '{}';
    try {
      return JSON.parse(announcementsReadLogs);
    } catch (e) {
      // If parsing fails, we return an empty object
      return {};
    }
  }

  hasSeenCampaignDashboardWalkthrough (): boolean {
    if (!this.account) {
      return true;
    }
    const key = `${CAMPAIGN_DASHBOARD_WALKTHROUGH_SEEN_STORAGE_KEY}_${this.account.id}`;
    return localStorage.getItem(key) === 'true';
  }

  markCampaignDashboardWalkthroughSeen (): void {
    if (!this.account) {
      return;
    }
    const key = `${CAMPAIGN_DASHBOARD_WALKTHROUGH_SEEN_STORAGE_KEY}_${this.account.id}`;
    localStorage.setItem(key, 'true');
  }
}
